import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { AnalyticsEventName, trackAmplitudeEvent } from '@schooly/analytics';
import {
  Assessment,
  DATE_FORMAT_MONTH_FNS,
  FilterKeys,
  GetAssessmentsQuerySort,
  GroupType,
  SORT_DIRECTION,
  useGetAssessmentsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { useInfiniteScroll } from '@schooly/hooks/use-infinite-scroll';
import {
  ExpandableSearch,
  GridRowCell,
  GridRowItem,
  GridRowName,
  GridRowStyled,
  GridViewIcon,
  PlusIcon,
  randomInt,
} from '@schooly/style';
import { useSelectIdsContext } from '@schooly/utils/bulk-actions';
import { newDateTimezoneOffset } from '@schooly/utils/date';
import { format } from 'date-fns';
import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { Counter } from '../../../../components/uikit/Counter/Counter.styled';
import { SimpleButton } from '../../../../components/uikit/SimpleButton/SimpleButton';
import { ListViewAssessments } from '../../../../components/uikit-components/ListViewAssessments/ListViewAssessments';
import {
  ModalContentStickyTitle,
  ModalContentStickyWrapper,
} from '../../../../components/uikit-components/Modal/Modal.styled';
import { MONTH_NONE } from '../../../../constants/misc';
import { useGroup } from '../../../../context/groups/useGroup';
import { GroupTabEmptyStub } from '../GroupTabEmptyStub';
import {
  GroupPreviewModalAssessmentsMonthName,
  GroupPreviewModalAssessmentsMonths,
} from './GroupPreviewModalAssessments.styled';

const PAGE_SIZE = 30;
const SKELETON_ROWS_COUNT = 6;

const DEFAULT_GROUP_ASSESSMENTS_SORT: GetAssessmentsQuerySort[] = [
  { columnTextId: 'assessment_date', direction: SORT_DIRECTION.DESC },
  { columnTextId: 'name', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'evaluation_type', direction: SORT_DIRECTION.ASC },
  { columnTextId: 'area_of_learning_name', direction: SORT_DIRECTION.ASC },
];

export const GroupPreviewModalAssessments: FC = () => {
  const { formatMessage } = useIntl();
  const { group, groupId = '' } = useGroup();
  const { permissions, relationId, schoolId = '' } = useAuth();

  const canCreate =
    group?.group_type !== GroupType.NoSubject &&
    (permissions?.includes('assessment_manager') ||
      (permissions?.includes('assessment_creator') &&
        group?.staff.some((u) => u.user.relation_id === relationId)));

  const { data, isLoading, params, setParams, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useGetAssessmentsQuery(
      {
        school_id: schoolId,
        filters: { [FilterKeys.Group]: [groupId] },
        query: '',
        sort: DEFAULT_GROUP_ASSESSMENTS_SORT,
        page_size: PAGE_SIZE,
      },
      { refetchOnMount: 'always', enabled: !!groupId && !!schoolId },
    );
  const total = data?.pages[0]?.count;

  const assessments = useMemo(
    () => data?.pages.reduce<Assessment[]>((prev, curr) => [...prev, ...curr.results], []) ?? [],
    [data?.pages],
  );
  const count = assessments.length;

  const assessmentsByMonths = useMemo(() => {
    return assessments.reduce<Record<string, Assessment[]>>((prev, assessment) => {
      const month = assessment.assessment_date
        ? format(
            newDateTimezoneOffset(assessment.assessment_date),
            DATE_FORMAT_MONTH_FNS,
          ).toUpperCase()
        : MONTH_NONE;

      if (!prev[month]) {
        prev[month] = [];
      }

      prev[month].push(assessment);

      return prev;
    }, {});
  }, [assessments]);

  const { selectedIds, toggleSelectId, clearSelectedIds } = useSelectIdsContext();

  const handleChangeQuery = useCallback(
    (query: string) => {
      setParams((p) => ({ ...p, query }));
      clearSelectedIds();
    },
    [setParams, clearSelectedIds],
  );

  const loaderRef = useInfiniteScroll(
    isLoading || isFetchingNextPage || !hasNextPage,
    fetchNextPage,
  );

  return (
    <ModalContentStickyWrapper>
      <ModalContentStickyTitle
        sx={(theme) => ({
          [theme.breakpoints.down('md')]: {
            '@media (orientation: portrait)': {
              paddingTop: 3,
            },
          },
        })}
      >
        <Stack
          direction="row"
          gap={2}
          justifyContent="space-between"
          mb={2.5}
          alignItems="center"
          sx={(theme) => ({
            flexWrap: 'wrap',
            [theme.breakpoints.only('md')]: {
              mb: assessments.length < 1 ? 0 : 2.5,
            },
          })}
        >
          <Typography variant="h2">
            <FormattedMessage id="section-Assessments" />
            {count > 1 && <Counter>{count}</Counter>}
          </Typography>

          <Stack
            direction="row"
            gap={2.5}
            sx={(theme) => ({
              [theme.breakpoints.down('md')]: {
                gap: theme.spacing(2),
              },
            })}
          >
            <ExpandableSearch
              value={params.query ?? ''}
              onChange_MemoizedCallbackOnly={handleChangeQuery}
              withDebounce
              placeholder={formatMessage({ id: 'people-Search' })}
            />

            {count > 1 && (
              <Stack
                component={Link}
                sx={{ color: 'text.primary' }}
                justifyContent="center"
                onClick={() => {
                  trackAmplitudeEvent({
                    name: AnalyticsEventName.MarkbookOpen,
                    payload: { group_name: group?.name },
                  });
                }}
                to="./markbook"
              >
                <SimpleButton startIcon={<GridViewIcon />} color="inherit">
                  <FormattedMessage id="groups-Assessment-OpenMarkbook" />
                </SimpleButton>
              </Stack>
            )}
            {canCreate && (
              <Stack
                component={Link}
                sx={{ color: 'text.primary' }}
                justifyContent="center"
                to={{ pathname: '/assessments/new' }}
                state={{ selectedGroups: [group] }}
              >
                <SimpleButton startIcon={<PlusIcon />} color="inherit">
                  <FormattedMessage id="groups-Assessment-CreateAssessment" />
                </SimpleButton>
              </Stack>
            )}
          </Stack>
        </Stack>
      </ModalContentStickyTitle>
      {isLoading ? (
        <>
          <Typography variant="h3" mb={0.5}>
            <Skeleton width={30} />
          </Typography>
          <GroupPreviewAssessmentsListSkeleton rowCount={SKELETON_ROWS_COUNT} />
        </>
      ) : (
        <GroupPreviewModalAssessmentsMonths>
          {assessments.length < 1 && (
            <GroupTabEmptyStub textId="groups-NoResultsMatching-Assessment" />
          )}
          {Object.keys(assessmentsByMonths).map((month) => (
            <Box key={month}>
              {month !== MONTH_NONE && (
                <GroupPreviewModalAssessmentsMonthName>
                  {month}
                </GroupPreviewModalAssessmentsMonthName>
              )}
              <ListViewAssessments
                assessments={assessmentsByMonths[month]}
                groupId={group?.id}
                selectedIds={selectedIds}
                onSelectAssessment={toggleSelectId}
              />
            </Box>
          ))}

          {hasNextPage && (
            <>
              {isFetchingNextPage && (
                <GroupPreviewAssessmentsListSkeleton
                  rowCount={Math.min(
                    PAGE_SIZE,
                    total && data ? total - data.pages.length * PAGE_SIZE : PAGE_SIZE,
                  )}
                />
              )}
              <Box ref={loaderRef} />
            </>
          )}
        </GroupPreviewModalAssessmentsMonths>
      )}
    </ModalContentStickyWrapper>
  );
};

const GroupPreviewAssessmentsListSkeleton = ({ rowCount }: { rowCount: number }) => {
  return (
    <Box>
      {[...new Array(rowCount)].map((_, i) => {
        return (
          <GridRowStyled sx={{ pointerEvents: 'none', overflow: 'hidden' }} key={i}>
            <GridRowItem gap={1} sx={{ padding: 1 }}>
              <GridRowCell minWidth={60} variant={'h3'}>
                <Skeleton width={30} />
              </GridRowCell>
              <GridRowName minWidth={200} variant={'h3'}>
                <Skeleton width={`${randomInt(50, 100)}%`} />
              </GridRowName>
              <GridRowCell minWidth={210} variant={'h3'}>
                <Skeleton width={`${randomInt(50, 100)}%`} />
              </GridRowCell>
              <GridRowCell minWidth={170} variant={'h3'}>
                <Skeleton width={`${randomInt(50, 100)}%`} />
              </GridRowCell>
            </GridRowItem>
          </GridRowStyled>
        );
      })}
    </Box>
  );
};
